(() => {
  const ROOT_SELECTOR = '[data-ag-review-authority]';

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const normalizeString = (value) => String(value ?? '').trim();
  const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const pickFirst = (object, keys) => {
    if (!object || typeof object !== 'object') return '';
    for (const key of keys) {
      const value = object[key];
      if (value != null && String(value).trim()) return value;
    }
    return '';
  };

  const responseReviews = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    if (Array.isArray(payload.reviews)) return payload.reviews;
    if (Array.isArray(payload.data)) return payload.data;
    if (payload.data && Array.isArray(payload.data.reviews)) return payload.data.reviews;
    return [];
  };

  const photoUrls = (review) => {
    const urls = [];
    for (const candidate of [review?.photos, review?.images, review?.media]) {
      if (!Array.isArray(candidate)) continue;
      for (const item of candidate) {
        if (typeof item === 'string' && item.trim()) {
          urls.push(item.trim());
        } else if (item && typeof item === 'object') {
          const url = [item.url, item.src, item.image, item.large, item.original, item.standard].find(
            (value) => typeof value === 'string' && value.trim(),
          );
          if (url) urls.push(url.trim());
        }
      }
    }
    return [...new Set(urls)];
  };

  const normalizeReview = (review, index) => {
    const reviewer = pickFirst(review, ['reviewer', 'reviewer_name', 'author', 'author_name', 'name']);
    const title = pickFirst(review, ['title', 'headline', 'review_title']);
    const body = pickFirst(review, ['body', 'content', 'review_body', 'text']);
    const date = pickFirst(review, ['date', 'created_at', 'published_at', 'submitted_at']);
    const id = pickFirst(review, ['id', 'review_id']) || `${reviewer}-${title}-${index}`;
    const ratingSource =
      review.rating ?? review.rating_value ?? review.score ?? review.stars ?? review.star_rating ?? review.review_rating;

    return {
      id: String(id),
      reviewer: normalizeString(reviewer) || 'Verified buyer',
      title: normalizeString(title),
      body: normalizeString(body),
      date: normalizeString(date),
      rating: Math.max(0, Math.min(5, toNumber(ratingSource))),
      photos: photoUrls(review),
    };
  };

  const formatDate = (value, locale) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    try {
      return new Intl.DateTimeFormat(locale || undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    } catch (_error) {
      return date.toISOString().slice(0, 10);
    }
  };

  const stars = (rating) => {
    const rounded = Math.max(0, Math.min(5, Math.round(rating)));
    return Array.from({ length: 5 }, (_, index) => (index < rounded ? '★' : '☆')).join('');
  };

  const initSalesCounter = (root) => {
    const counter = root.querySelector('[data-ag-sales-counter]');
    if (!counter || counter.dataset.agSalesCounterInit === 'true') return;
    counter.dataset.agSalesCounterInit = 'true';

    const target = Math.max(0, Math.floor(toNumber(counter.dataset.countTarget)));
    if (!target) return;

    const locale = root.dataset.locale || undefined;
    const formatter = new Intl.NumberFormat(locale);
    const targetCharacters = Array.from(formatter.format(target));
    const targetDigitCount = targetCharacters.filter((character) => /\p{Number}/u.test(character)).length;
    const render = (value) => {
      const digits = String(Math.max(0, Math.floor(value))).padStart(targetDigitCount, '0').split('');
      let digitIndex = 0;
      counter.innerHTML = `${targetCharacters
        .map((character) => {
          if (/\p{Number}/u.test(character)) {
            const digit = digits[digitIndex++] || '0';
            return `<span class="ag-review-authority__counter-flap" aria-hidden="true">${digit}</span>`;
          }
          return `<span class="ag-review-authority__counter-separator" aria-hidden="true">${escapeHtml(character)}</span>`;
        })
        .join('')}`;
      counter.setAttribute('aria-label', formatter.format(Math.max(0, Math.floor(value))));
    };

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      render(target);
      return;
    }

    render(0);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();

        const duration = 1600;
        const startedAt = performance.now();
        const animate = (now) => {
          const progress = Math.min(1, (now - startedAt) / duration);
          const easedProgress = 1 - Math.pow(1 - progress, 3);
          render(Math.round(target * easedProgress));
          if (progress < 1) window.requestAnimationFrame(animate);
        };
        window.requestAnimationFrame(animate);
      },
      { threshold: 0.25 },
    );
    observer.observe(root);
  };

  const fetchProductReviews = async (root, productId, options) => {
    const url = new URL(root.dataset.proxyPath, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('locale', options.locale);
    url.searchParams.set('limit', String(options.proxyFetchLimit));
    url.searchParams.set('min_rating', String(options.minRating));

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeout = controller ? window.setTimeout(() => controller.abort(), options.timeoutMs) : null;

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: controller?.signal,
      });
      if (!response.ok) return [];
      return responseReviews(await response.json());
    } catch (_error) {
      return [];
    } finally {
      if (timeout) window.clearTimeout(timeout);
    }
  };

  const renderSlide = (review, index, options) => {
    const photo = escapeHtml(review.photos[0]);
    const title = escapeHtml(review.title);
    const body = escapeHtml(review.body);
    const reviewer = escapeHtml(review.reviewer);
    const date = options.showDate ? escapeHtml(formatDate(review.date, options.locale)) : '';
    const rating = stars(review.rating);
    const meta = [reviewer, date].filter(Boolean);
    const bodyId = `${options.sectionId}-review-body-${index}`;

    return `
      <article
        class="ag-review-authority__slide"
        role="group"
        aria-roledescription="slide"
        aria-label="${index + 1} of ${options.total}"
      >
        <div class="ag-review-authority__media">
          <img
            class="ag-review-authority__image"
            src="${photo}"
            alt="${escapeHtml(review.title || `Customer review photo from ${review.reviewer}`)}"
            loading="${index === 0 ? 'eager' : 'lazy'}"
          >
        </div>
        <div class="ag-review-authority__content">
          <p class="ag-review-authority__rating" aria-label="${Math.round(review.rating)} out of 5 stars">${rating}</p>
          ${title ? `<h2 class="ag-review-authority__title" data-ag-review-title>${title}</h2>` : ''}
          ${body ? `<p class="ag-review-authority__body" id="${bodyId}" data-ag-review-body>${body}</p>` : ''}
          ${
            body
              ? `<button
                  class="ag-review-authority__view-more"
                  type="button"
                  aria-controls="${bodyId}"
                  aria-expanded="false"
                  data-ag-review-view-more
                  hidden
                >${escapeHtml(options.viewMoreLabel)}</button>`
              : ''
          }
          ${
            meta.length
              ? `<p class="ag-review-authority__meta"><span class="ag-review-authority__reviewer">${meta[0]}</span>${
                  meta[1] ? `<span aria-hidden="true">•</span><span>${meta[1]}</span>` : ''
                }</p>`
              : ''
          }
        </div>
      </article>
    `;
  };

  const init = async (root) => {
    if (root.dataset.agReviewAuthorityInit === 'true') return;
    root.dataset.agReviewAuthorityInit = 'true';
    initSalesCounter(root);

    const track = root.querySelector('[data-ag-review-track]');
    const viewport = root.querySelector('[data-ag-review-viewport]');
    const dots = root.querySelector('[data-ag-review-dots]');
    const status = root.querySelector('[data-ag-review-status]');
    const idsNode = root.querySelector('[data-ag-review-product-ids]');
    if (!track || !viewport || !dots || !status || !idsNode) return;

    let productIds = [];
    try {
      productIds = JSON.parse(idsNode.textContent || '[]').map(String).filter(Boolean);
    } catch (_error) {
      productIds = [];
    }

    if (!root.dataset.proxyPath || !productIds.length) {
      status.textContent = 'Select a collection containing products with customer reviews.';
      return;
    }

    const options = {
      locale: root.dataset.locale || '',
      reviewsPerProduct: Math.max(1, Number(root.dataset.reviewsPerProduct || 3)),
      minRating: Math.max(1, Number(root.dataset.minRating || 4)),
      timeoutMs: Math.max(500, Number(root.dataset.fetchTimeoutMs || 9500)),
      showDate: root.dataset.showDate === 'true',
      sectionId: root.id || 'ag-review-authority',
      viewMoreLabel: root.dataset.viewMoreLabel || 'Show more',
      viewLessLabel: root.dataset.viewLessLabel || 'Show less',
    };
    options.proxyFetchLimit = Math.max(options.reviewsPerProduct, Math.min(20, options.reviewsPerProduct * 3));

    const responses = await Promise.all(productIds.map((id) => fetchProductReviews(root, id, options)));
    const seen = new Set();
    const normalizedReviews = responses
      .flat()
      .map(normalizeReview)
      .filter((review) => review.rating >= options.minRating && (review.title || review.body));
    const photoReviews = normalizedReviews.filter((review) => review.photos.length);
    const reviews = photoReviews
      .filter((review) => {
        if (seen.has(review.id)) return false;
        seen.add(review.id);
        return true;
      })
      .sort((a, b) => {
        const aTime = new Date(a.date || 0).getTime() || 0;
        const bTime = new Date(b.date || 0).getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, Math.max(2, Number(root.dataset.reviewLimit || 5)));

    window.__agReviewAuthorityDebug = {
      productIds,
      responseCounts: responses.map((response) => response.length),
      normalizedCount: normalizedReviews.length,
      photoReviewCount: photoReviews.length,
      renderedCount: reviews.length,
      options,
    };

    if (!reviews.length) {
      status.textContent = 'No customer reviews with images are available yet.';
      return;
    }

    const renderOptions = { ...options, total: reviews.length };
    track.innerHTML = reviews.map((review, index) => renderSlide(review, index, renderOptions)).join('');
    dots.innerHTML = reviews
      .map(
        (_review, index) => `
          <button
            class="ag-review-authority__dot"
            type="button"
            aria-label="Show review ${index + 1}"
            aria-current="${index === 0 ? 'true' : 'false'}"
            data-ag-review-dot="${index}"
          ></button>
        `,
      )
      .join('');
    dots.hidden = reviews.length < 2;
    status.hidden = true;

    const dotButtons = [...dots.querySelectorAll('[data-ag-review-dot]')];
    const slides = [...track.querySelectorAll('.ag-review-authority__slide')];
    const viewMoreButtons = [...track.querySelectorAll('[data-ag-review-view-more]')];
    let activeIndex = 0;
    let timer = null;

    const setExpanded = (slide, expanded) => {
      if (!slide) return;
      slide.classList.toggle('is-expanded', expanded);
      const button = slide.querySelector('[data-ag-review-view-more]');
      if (!button) return;
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? options.viewLessLabel : options.viewMoreLabel;
    };

    const syncMobileToggles = () => {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      slides.forEach((slide) => {
        setExpanded(slide, false);
        const button = slide.querySelector('[data-ag-review-view-more]');
        const title = slide.querySelector('[data-ag-review-title]');
        const body = slide.querySelector('[data-ag-review-body]');
        if (!button || !body) return;
        const hasOverflow =
          isMobile &&
          (body.scrollHeight > body.clientHeight + 1 || (title && title.scrollHeight > title.clientHeight + 1));
        button.hidden = !hasOverflow;
      });
    };

    const setActive = (index, shouldScroll = false) => {
      const nextIndex = Math.max(0, Math.min(reviews.length - 1, index));
      if (nextIndex !== activeIndex) setExpanded(slides[activeIndex], false);
      activeIndex = nextIndex;
      dotButtons.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === activeIndex)));
      if (shouldScroll) {
        track.scrollTo({ left: activeIndex * track.clientWidth, behavior: 'smooth' });
      }
    };

    const stopAutoplay = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (root.dataset.autoplay !== 'true' || reviews.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const delay = Math.max(3, Number(root.dataset.autoplaySeconds || 6)) * 1000;
      timer = window.setInterval(() => setActive((activeIndex + 1) % reviews.length, true), delay);
    };

    viewMoreButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const slide = button.closest('.ag-review-authority__slide');
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        setExpanded(slide, expanded);
        if (expanded) stopAutoplay();
        else startAutoplay();
      });
    });

    dotButtons.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        setActive(index, true);
        startAutoplay();
      });
    });

    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          setActive(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
        });
      },
      { passive: true },
    );

    viewport.addEventListener('mouseenter', stopAutoplay);
    viewport.addEventListener('mouseleave', startAutoplay);
    viewport.addEventListener('focusin', stopAutoplay);
    viewport.addEventListener('focusout', startAutoplay);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    let resizeFrame = 0;
    window.addEventListener(
      'resize',
      () => {
        if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(syncMobileToggles);
      },
      { passive: true },
    );

    syncMobileToggles();
    startAutoplay();
  };

  const initAll = () => document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
